<template>
  <CItem :icon="workStatusIcon" :title="job.getTitle()" class="c-job" :class="rootClasses">
    <template #left>
      <slot name="left"/>
    </template>

    <template v-if="isAnyOf(display, 'extended', 'full', 'time-registration', 'job-overview')" #body>
      <div class="c-job-body">
        <p v-if="job.pauseReason?.description && !isAnyOf(display, 'time-registration', 'job-overview')">
          {{ job.pauseReason.description }}
        </p>

        <CJobComplaint v-if="isAnyOf(display, 'full', 'job-overview')" :job :context :mediaContext/>

        <CJobTags v-if="display !== 'extended'" :job/>
      </div>
    </template>

    <template v-if="$slots.right" #right>
      <slot name="right"/>
    </template>

    <template v-else-if="!isBookingItem && isAnyOf(display, 'statuses', 'full')" #right>
      <CJobStatuses :job/>
    </template>
  </CItem>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import MediaContext2 from '@/general/models/MediaContext2';
  import CItem from '@/shared/components/CItem.vue';
  import { isAnyOf } from '@/shared/utils/typescript';
  import JobCategory from '../constants/JobCategory';
  import Job from '../models/Job';
  import JobContext from '../models/JobContext';
  import { getWorkStatusIcon } from '../utils/icons';
  import CJobComplaint from './CJobComplaint.vue';
  import CJobStatuses from './CJobStatuses.vue';
  import CJobTags from './CJobTags.vue';

  /**
   * undefined = only basic info (title and icon)
   * 'statuses' = basic info + statuses
   * 'extended' = everything except tags and statuses
   * 'full' = everything
   * 'time-registration' = everything except pauseReason and statuses
   * 'job-overview' = basic info + statuses + complaint
   */
  export type Display = 'statuses' | 'extended' | 'full' | 'time-registration' | 'job-overview';

  const props = defineProps<{
    job: Job;
    display?: Display;
    context?: JobContext;
    mediaContext?: MediaContext2;
  }>();

  const isBookingItem = computed(() => props.job.getCategory() === JobCategory.BOOKING_ITEM);

  const workStatusIcon = computed(() => {
    if (isBookingItem.value) return 'empty';
    return getWorkStatusIcon(props.job.workStatus);
  });

  const rootClasses = computed(() => ({
    'c-job-ready': props.job.isReady(),
    'c-job-booking-item': isBookingItem.value,
  }));
</script>

<style lang="scss">
  .c-job {
    .c-item-body {
      margin-top: 0.25rem;
      display: flex;
      gap: 0.25rem;
    }

    .c-item-icon {
      color: $title-fg;
    }

    &-ready .c-item-title, &-ready .c-item-icon, &-booking-item .c-item-title {
      color: $neutral-600;
    }

    &-body {
      & > *:not(:last-child) {
        margin-bottom: 0.5rem;
      }
    }

    &-booking-item {
      .c-item-title {
        font-weight: initial;
        font-style: italic;
      }
    }
  }
</style>